import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import {
  borderRadius,
  zIndex,
  breakpoints,
  fontWeight,
  spacing,
  duration,
  fontSize,
  lineHeight,
  status,
  surface,
  text,
} from "@atlas-int/tokens";
import { Button, CloseButton as BaseCloseButton } from "@atlas-int/ui";
import LearnIcon from "./img/learn.svg?react";
import DiagramImage from "./img/Diagram.png";
import AustriaPs from "./img/Austria_ps.png";
import AustraliaPs from "./img/Australia_ps.png";
import CloseIcon from "@mui/icons-material/Close";
import { NodeColorLegend } from "@atlas-int/viz/productspace";
import AustriaFlag from "./img/Austria.svg";
import AustraliaFlag from "./img/Australia.svg";
import AnimatedProductSpace from "./AnimatedProductSpace";
import { ParentSize } from "@visx/responsive";
import { useQuery } from "@apollo/client/react";
import { CPY_GROWTH_QUERY } from "@atlas-int/data";
const query = CPY_GROWTH_QUERY;
import type { Cpy_GrowthQuery, Cpy_GrowthQueryVariables } from "@atlas-int/data/graphql";
import { ProductClass, ServicesClass } from "@atlas-int/data/graphql";
import hoverLarge from "./img/hover_large.gif";
import clickLarge from "./img/click_large.gif";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTutorial } from "./TutorialContext";
import { useDataAvailability } from "@atlas-int/data";

const TutorialContainer = styled.div<{ currentStep: number }>`
  width: calc(100% - 22px);
  height: calc(100% - 22px);
  margin: ${spacing[4]};
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: auto;
  padding: clamp(0.5rem, 2vw, 1rem);
  padding-bottom: calc(clamp(0.5rem, 2vw, 1rem) + 12px);
  z-index: ${zIndex.navigation};
  border: 0.5px solid ${text.secondary};
  box-sizing: border-box;
  max-height: 100vh;
  background-color: ${(props) => (props.currentStep > 0 ? surface.white : surface.panel)};

  @media (max-width: ${breakpoints.sm}) {
    margin: 5px;
    width: calc(100% - 10px);
    height: calc(100% - 10px);
  }
`;

const Section = styled.div`
  margin: clamp(0.1rem, 1vh, 0.15rem) 0;
`;

const TopSection = styled(Section)`
  flex: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  width: 100%;
  height: 100%;
  max-height: 60vh;
  position: relative;
`;

const BottomSection = styled(Section)`
  display: flex;
  flex: 1;
  min-height: auto;
  max-height: 25vh;
  overflow-y: auto;
  align-items: center;
  justify-content: center;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: clamp(0.5rem, 2vw, 1rem);
  margin-bottom: clamp(0.25rem, 1.5vh, 0.5rem);
  text-align: center;
  flex-shrink: 0;

  @media (max-width: ${breakpoints.sm}) {
    flex-direction: column;
  }
`;

const LightbulbIcon = styled.div`
  color: ${status.gold};
  height: 60px;
  display: flex;
  align-items: flex-start;
  padding-top: ${spacing[2]};
  svg {
    width: 100%;
    height: 100%;
    fill: ${status.gold};
  }
`;

const Title = styled.h1`
  font-size: ${fontSize["3xl"]};
  font-weight: ${fontWeight.normal};
  color: ${text.primary};
  margin: 0;
  line-height: ${lineHeight.tight};
  max-width: 1000px;

  @media (max-width: ${breakpoints.sm}) {
    font-size: ${fontSize["3xl"]};
  }
  text-align: left;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(0.5rem, 1vh, 0.75rem);

  flex-shrink: 0;
`;

const LearnButton = styled(Button)`
  font-weight: ${fontWeight.semibold};
  padding: 0.75rem 2rem;
`;

const SkipButton = styled(Button)`
  border-color: ${text.secondary};
  color: ${text.secondary};

  &:hover:not(:disabled) {
    background: ${surface.panel};
  }
`;

const Description = styled.p`
  color: ${text.primary};
  margin: 0.1rem 0;
  overflow-wrap: break-word;
  max-width: 1000px;
  font-size: clamp(2px, 2vw, 22px);
  line-height: ${lineHeight.normal};
`;

const DiagramImageContainer = styled.div<{ isAnimated?: boolean }>`
  position: relative;
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  ${(props) =>
    props.isAnimated
      ? `
    & > div {
      position: absolute !important;
      inset: 0;
      width: 100% !important;
      height: 100% !important;
    }
  `
      : `
    img {
      max-width: 100%;
      max-height: 100%;
      width: auto;
      height: auto;
      object-fit: contain;
    }
  `}
`;

const CloseButton = styled(BaseCloseButton)`
  position: absolute;
  top: 1rem;
  right: 1rem;
  color: ${text.primary};
  width: 24px;
  height: 24px;
  border-radius: ${borderRadius.full};
  z-index: ${zIndex.glossaryOverlay};

  &:hover {
    background: ${surface.panel};
    opacity: 1;
  }
`;

const ProgressBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 12px;
  background-color: color-mix(in srgb, ${status.gold} 30%, transparent);
`;

const ProgressFill = styled.div<{ progress: number }>`
  height: 100%;
  background-color: ${status.gold};
  width: ${(props) => props.progress}%;
  transition: width ${duration.slow} ease;
`;

interface TutorialStep {
  title?: string;
  bottomContent: React.ReactNode;
}

const AustriaText = () => (
  <div style={{ display: "flex", alignItems: "center", lineHeight: lineHeight.tight }}>
    <div>
      <img
        style={{ height: spacing[10], paddingRight: spacing[4] }}
        src={AustriaFlag}
        alt="Austria Flag"
      />{" "}
    </div>
    Austria in the product space
  </div>
);

const NavigationButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;

  button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
  }

  @media (max-width: ${breakpoints.sm}) {
    gap: 0.5rem;
    width: 100%;

    button {
      flex: 1;
    }
  }
`;

interface ProductSpaceTutorialProps {
  open: boolean;
  onClose: () => void;
}

// TUTORIAL_QUERY_PARAMS is now generated inside the component to use dynamic year data

const AnimatedProductSpaceContainer = styled.div`
  display: grid;
  grid-template-columns: 200px 1fr 200px;
  width: 100%;
  height: 100%;
  position: absolute;
  inset: 0;
  gap: 1rem;

  @media (max-width: ${breakpoints.sm}) {
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr auto;
  }
`;

const SidePanel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  height: 100%;
  overflow: auto;
`;

const ComparisonContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  width: 100%;
  height: 100%;
  position: relative;

  @media (max-width: ${breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const CountryContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-height: 0;
`;

const ImageWrapper = styled.div`
  flex: 1;
  width: 100%;
  min-height: 0;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
  }
`;

const CountryLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem;
  font-size: 0.9rem;
  color: ${text.primary};
  flex-shrink: 0;
  line-height: ${lineHeight.tight};
  font-weight: ${fontWeight.semibold};

  img {
    height: 16px;
    width: auto;
  }
`;

const ProductSpaceTutorial: React.FC<ProductSpaceTutorialProps> = ({
  open,
  onClose,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const { markTutorialComplete, dismissTutorial } = useTutorial();
  const { getLatestYear } = useDataAvailability();
  const latestYear = getLatestYear();

  const TUTORIAL_QUERY_PARAMS: Cpy_GrowthQueryVariables = {
    productClass: ProductClass.Hs12,
    servicesClass: ServicesClass.Unilateral,
    countryId: 40,
    productLevel: 4,
    yearMin: latestYear || 2024,
    yearMax: latestYear || 2024,
    hs12: true,
    hs22: false,
    hs92: false,
    sitc: false,
  };

  const { data } = useQuery<Cpy_GrowthQuery, Cpy_GrowthQueryVariables>(query, {
    variables: TUTORIAL_QUERY_PARAMS,
    skip: !latestYear, // Skip query until we have the year data
  });

  useEffect(() => {
    if (open) {
      const imagesToPreload = [
        DiagramImage,
        AustriaPs,
        AustraliaPs,
        hoverLarge,
        clickLarge,
      ];

      imagesToPreload.forEach((imageSrc) => {
        const img = new Image();
        img.src = imageSrc;
      });
    }
  }, [open]);

  const renderTopContent = () => {
    if (currentStep === 0) {
      return (
        <DiagramImageContainer isAnimated={false}>
          <img
            style={{ maxWidth: "80%", maxHeight: "80%" }}
            src={DiagramImage}
            alt="Product Space Introduction"
          />
        </DiagramImageContainer>
      );
    }

    if (currentStep === 5) {
      return (
        <DiagramImageContainer>
          <ComparisonContainer>
            <CountryContainer>
              <ImageWrapper>
                <img src={AustriaPs} alt="Austria Product Space" />
              </ImageWrapper>
              <CountryLabel>
                <img src={AustriaFlag} alt="Austria Flag" />
                Austria in the Product Space
              </CountryLabel>
            </CountryContainer>

            <CountryContainer>
              <ImageWrapper>
                <img src={AustraliaPs} alt="Australia Product Space" />
              </ImageWrapper>
              <CountryLabel>
                <img src={AustraliaFlag} alt="Australia Flag" />
                Australia in the Product Space
              </CountryLabel>
            </CountryContainer>
          </ComparisonContainer>
        </DiagramImageContainer>
      );
    }
    if (currentStep === 6) {
      return (
        <DiagramImageContainer>
          <img src={hoverLarge} alt="Hover interaction demo" />
        </DiagramImageContainer>
      );
    }
    if (currentStep === 7) {
      return (
        <DiagramImageContainer>
          <img src={clickLarge} alt="Click interaction demo" />
        </DiagramImageContainer>
      );
    }

    return (
      <AnimatedProductSpaceContainer>
        <SidePanel>
          {currentStep === 4 && (
            <div style={{ alignSelf: "flex-start" }}>
              <AustriaText />
            </div>
          )}
        </SidePanel>

        <DiagramImageContainer isAnimated={true}>
          <ParentSize>
            {({ width, height }) =>
              width && height ? (
                <AnimatedProductSpace
                  step={currentStep - 1}
                  width={width}
                  height={height}
                  data={data as import("./AnimatedProductSpace").TutorialGrowthData | undefined}
                />
              ) : null
            }
          </ParentSize>
        </DiagramImageContainer>

        <SidePanel>{currentStep === 4 && <NodeColorLegend />}</SidePanel>
      </AnimatedProductSpaceContainer>
    );
  };

  const tutorialSteps: TutorialStep[] = [
    {
      title:
        "Learn about the Product Space and navigate your potential —from current capabilities toward prosperity.",
      bottomContent: null,
    },
    {
      bottomContent: (
        <Description>
          In the Product Space, each node represents a product.  Here, we see
          more than 800 products traded around the world.
        </Description>
      ),
    },
    {
      bottomContent: (
        <Description>
          <b>Connected by capability:</b> Products requiring similar
          capabilities cluster together in the space, as they are likely to be
          produced in the same place. Products that require distinct
          capabilities are more distant in the space.
        </Description>
      ),
    },
    {
      bottomContent: (
        <Description>
          <b>Products are sized by global export volume.</b> Note that the
          location of each node remains fixed in the Product Space, regardless
          of which country is selected.
        </Description>
      ),
    },
    {
      bottomContent: (
        <Description>
          <b>Proximity signals possibility:</b> By examining a country in the
          Product Space, we can identify how its current strengths connect to
          future growth pathways.
        </Description>
      ),
    },
    {
      bottomContent: (
        <Description>
          <b>Different starting points, different trajectories:</b> By comparing
          2 countries - Austria and Australia - the Product Space reveals how
          distinct capabilities shape unique paths to diversification.
        </Description>
      ),
    },
    {
      bottomContent: (
        <Description>
          <b>Navigate your potential:</b> By hovering over a product, you can
          explore its connections to nearby products.
        </Description>
      ),
    },
    {
      bottomContent: (
        <Description>
          <b>Unlock your next opportunity:</b> By clicking on a product, you can
          view its related products in a ring chart. Here we see that Austria's
          strength in tractors offers a related opportunity to produce vehicle
          bodies, as they require similar capabilities.
        </Description>
      ),
    },
  ];

  useEffect(() => {
    if (!open) {
      setCurrentStep(0);
    }
  }, [open]);

  const handleNext = () => {
    if (currentStep === 0) {
      markTutorialComplete();
    }
    if (currentStep < tutorialSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onClose();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleClose = () => {
    dismissTutorial();
    onClose();
  };

  if (!open) return null;

  return (
    <TutorialContainer currentStep={currentStep}>
      {currentStep > 0 && (
        <CloseButton onClick={handleClose}>
          <CloseIcon sx={{ fontSize: 34 }} />
        </CloseButton>
      )}
      {tutorialSteps[currentStep].title && (
        <Header>
          <LightbulbIcon>
            <LearnIcon />
          </LightbulbIcon>
          <Title>{tutorialSteps[currentStep].title}</Title>
        </Header>
      )}

      <TopSection>{renderTopContent()}</TopSection>
      {tutorialSteps[currentStep].bottomContent && (
        <BottomSection>
          {tutorialSteps[currentStep].bottomContent}
        </BottomSection>
      )}

      <ButtonContainer>
        <NavigationButtons>
          {currentStep > 0 && (
            <SkipButton variant="outline" onClick={handlePrevious}>
              <ArrowBackIcon sx={{ fontSize: 20 }} />
              Previous
            </SkipButton>
          )}
          <LearnButton variant="gold" onClick={handleNext}>
            {currentStep === tutorialSteps.length - 1 ? (
              "Get Started"
            ) : currentStep === 0 ? (
              "LET'S LEARN"
            ) : (
              <>
                Next
                <ArrowForwardIcon sx={{ fontSize: 20 }} />
              </>
            )}
          </LearnButton>
        </NavigationButtons>
        {currentStep === 0 && (
          <SkipButton variant="outline" onClick={handleClose}>SKIP THE TUTORIAL</SkipButton>
        )}
      </ButtonContainer>
      <ProgressBar>
        <ProgressFill
          progress={(currentStep / (tutorialSteps.length - 1)) * 100}
        />
      </ProgressBar>
    </TutorialContainer>
  );
};

export default ProductSpaceTutorial;
